import {
  computeCardTotals,
  formatShortMonthLabel,
  formatUtilizationAmount,
  getUtilizationPercentColor,
} from "./utilizationReportUtils";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const formatPrintDateTimeIst = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type) => parts.find((part) => part.type === type)?.value || "";
  return `${get("day")}/${get("month")}/${get("year")}, ${get("hour")}:${get("minute")}:${get("second")} IST`;
};

const amountCell = (value) =>
  `<td class="num">${escapeHtml(formatUtilizationAmount(value))}</td>`;

const percentCell = (percent) => {
  const safePercent = Math.max(0, Math.min(100, Number(percent) || 0));
  const { color } = getUtilizationPercentColor(safePercent);
  return `<td class="num percent"><span style="color:${color};font-weight:700;">${safePercent}%</span></td>`;
};

const buildDataRow = (row) => `
  <tr>
    <td class="center">${escapeHtml(row.slNo)}</td>
    <td class="left">${escapeHtml(row.rowLabel)}</td>
    ${amountCell(row.prevFy.totalBudget)}
    ${amountCell(row.prevFy.totalExpense)}
    ${amountCell(row.prevFy.totalBalance)}
    ${amountCell(row.selectedFy.totalBudget)}
    ${amountCell(row.selectedFy.expenseUptoPrevMonth)}
    ${amountCell(row.selectedFy.expenseForMonth)}
    ${amountCell(row.selectedFy.totalExpense)}
    ${percentCell(row.selectedFy.expensePercentOfBudget)}
    ${amountCell(row.selectedFy.totalBalance)}
    ${amountCell(row.cumulative.totalExpense)}
    ${amountCell(row.cumulative.totalBalance)}
  </tr>
`;

const buildCardTable = (card, meta, showSectionStamp) => {
  const totalRow = computeCardTotals(card.rows);
  const rowsHtml = [
    totalRow ? buildDataRow({ ...totalRow, slNo: "" }) : "",
    ...card.rows.map((row) => buildDataRow(row)),
  ].join("");

  const sectionStamp = showSectionStamp
    ? `<div class="section-stamp">As on Print Date (${escapeHtml(
        formatPrintDateTimeIst()
      )})</div>`
    : "";

  return `
    ${sectionStamp}
    <div class="card-block">
      <div class="head-banner">${escapeHtml(card.title)}</div>
      <table>
        <thead>
          <tr class="group-row">
            <th rowspan="2">Sl No</th>
            <th rowspan="2">${escapeHtml(meta.rowColumnLabel)}</th>
            <th colspan="3" class="section-prev">Total of Previous Financial Year (${escapeHtml(meta.previousFyLabel)})</th>
            <th colspan="6" class="section-selected">Total of Selected Financial Year (${escapeHtml(meta.selectedFyLabel)}) as on (${escapeHtml(meta.asOnDate)})</th>
            <th colspan="2" class="section-cumulative">Cumulative Total as on (${escapeHtml(meta.asOnDate)})</th>
          </tr>
          <tr class="subhead-row">
            <th>Total Budget</th>
            <th>Total Expense</th>
            <th>Total Balance</th>
            <th>Total Budget</th>
            <th>Total Expense upto month (${escapeHtml(formatShortMonthLabel(meta.previousMonthLabel))})</th>
            <th>Total Expense for month (${escapeHtml(formatShortMonthLabel(meta.selectedMonthLabel))})</th>
            <th>Total Expense</th>
            <th>Total Expense % of Budget</th>
            <th>Total Balance</th>
            <th>Total Expense (Cumulative)</th>
            <th>Total Balance (Cumulative)</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHtml}
        </tbody>
      </table>
    </div>
  `;
};

export const buildUtilizationReportPrintHtml = (
  reportData,
  { departmentName = "", userName = "" } = {}
) => {
  const { meta, cards = [] } = reportData;
  const printStamp = formatPrintDateTimeIst();
  const monthReportLabel = meta.selectedMonthLabel || "-";
  const fyLabel = meta.selectedFyLabel || "-";

  const tablesHtml = cards
    .map((card, index) => buildCardTable(card, meta, index > 0))
    .join("");

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Consolidated Financial Progress</title>
    <style>
      * { box-sizing: border-box; }
      body {
        font-family: Arial, Helvetica, sans-serif;
        margin: 0;
        padding: 16px 20px 28px;
        color: #111;
        background: #fff;
      }
      .toolbar {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-bottom: 16px;
      }
      .toolbar button {
        background: #198754;
        color: #fff;
        border: none;
        border-radius: 4px;
        padding: 8px 16px;
        font-size: 14px;
        cursor: pointer;
      }
      .report-header {
        text-align: center;
        margin-bottom: 18px;
      }
      .report-header h1 {
        margin: 0 0 8px;
        font-size: 20px;
        font-weight: 700;
      }
      .report-header h2 {
        margin: 0 0 6px;
        font-size: 16px;
        font-weight: 700;
      }
      .report-header p {
        margin: 0;
        font-size: 12px;
      }
      .section-stamp {
        text-align: center;
        font-size: 11px;
        margin: 18px 0 8px;
      }
      .card-block {
        margin-bottom: 22px;
        page-break-inside: avoid;
      }
      .head-banner {
        background: #ffc107;
        border: 1px solid #111;
        border-bottom: none;
        text-align: center;
        font-weight: 700;
        font-size: 13px;
        padding: 6px 8px;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
        font-size: 9px;
      }
      th, td {
        border: 1px solid #111;
        padding: 4px 3px;
        vertical-align: middle;
        word-wrap: break-word;
      }
      thead th {
        font-weight: 700;
        text-align: center;
      }
      .group-row th {
        color: #fff;
        font-size: 8px;
      }
      .subhead-row th {
        background: #eceff3;
        font-size: 8px;
      }
      .section-prev { background: #4a78b8; }
      .section-selected { background: #198754; }
      .section-cumulative { background: #e67e22; }
      tbody tr:first-child td {
        font-weight: 700;
        background: #f3f4f6;
      }
      tbody tr:nth-child(even):not(:first-child) td { background: #fafafa; }
      .center { text-align: center; }
      .left { text-align: left; }
      .num { text-align: center; white-space: nowrap; }
      .report-footer {
        margin-top: 28px;
        font-size: 10px;
        line-height: 1.5;
      }
      .report-footer .end {
        text-align: center;
        font-weight: 700;
        margin-top: 16px;
      }
      @media print {
        body { padding: 0; }
        .no-print { display: none !important; }
        @page { size: landscape; margin: 10mm; }
      }
    </style>
  </head>
  <body>
    <div class="toolbar no-print">
      <button type="button" onclick="window.print()">Export to PDF</button>
    </div>

    <div class="report-header">
      <h1>Consolidated Financial Progress Under ${escapeHtml(departmentName || "Department")}</h1>
      <h2>Monthly Progress Report - ${escapeHtml(monthReportLabel)} (FY-${escapeHtml(fyLabel)})</h2>
      <p>As on Print Date (${escapeHtml(printStamp)})</p>
    </div>

    ${tablesHtml}

    <div class="report-footer">
      <p>
        This document is property of the department and any distribution through any channel is prohibited
        unless approval taken from the appropriate competent authority.
        Download Source: SNA-FMS ${escapeHtml(userName)}
      </p>
      <div class="end">END OF REPORT</div>
    </div>
  </body>
</html>`;
};

export const openUtilizationReportPrintWindow = (reportData, options = {}) => {
  const html = buildUtilizationReportPrintHtml(reportData, options);
  const printWindow = window.open("", "_blank");

  if (!printWindow) {
    return false;
  }

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();

  setTimeout(() => {
    printWindow.print();
  }, 300);

  return true;
};
